import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Citizenship, Partner, Picture, User, UserCategory, Veteran } from '@app/entity';
import { FieldDescription } from './user-form.interface';
import { veteranFields } from './veteran.fields';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthService, FederalSubjectsService } from '@app/core/services';
import { flatten, head } from '@app/shared/utils';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { partnerFields } from './partner.fields';
import { City } from '@app/entity/city';
import { Router } from '@angular/router';
import { FamilyRecord } from '@app/accounts/family-input/family-input.component';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: User;
  @Output() updatedSuccessfuly = new EventEmitter<null>();

  form: FormGroup;
  fields: FieldDescription[];

  citizenships: Observable<Citizenship[]> = this.federalSubjectsService.getCitizenships();
  categories: Observable<UserCategory[]>;
  cities: Observable<City[]> = this.federalSubjectsService.cities;
  currentCities: City[] = [];

  apiErrors = {};
  error: string = null;

  isLoading = false;
  isUploadingPhoto = false;

  maxDate: Date;
  birthDateValue: Date;

  family: FamilyRecord[] = [];
  familyErrors = {};

  ngUnsubscribe = new Subject();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private federalSubjectsService: FederalSubjectsService,
    private router: Router,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.user) {
      this.initForm(this.user);
      this.categories = this.authService.getCategories(this.user.user_type);
    }
  }

  ngOnInit() {
    this.maxDate = moment().toDate();
    this.initForm(this.user);
    if (this.user) this.categories = this.authService.getCategories(this.user.user_type);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initForm(user: User) {
    if (!user) return;

    const fields = user && user.user_type === 'veteran' ? veteranFields : partnerFields;

    if (this.fields !== fields) {
      this.fields = fields;

      const controls = {};

      for (const field of this.fields) {
        controls[field.name] = ['', ];
      }

      this.form = this.fb.group({...controls, id: ['', ]});
      // need patch only when user_type changes because successful patch do redirect from this component
      this.form.patchValue({ ...user });
      const veteran = <Veteran>user;
      if (veteran && veteran.family) this.family = veteran.family;
    }

    if (user.user_type === 'partner') this.currentCities = [ ...this.joinCities((<Partner>user).city_ids, (<Partner>user).cities) ];

    if (user.user_type === 'veteran') {
      const birthDate = this.form.get('birth_date').value;
      this.birthDateValue = birthDate ? moment(birthDate).toDate() : null;
    }
  }

  onChangeBirthDate() {
    if (!this.birthDateValue) return;
    this.form.patchValue({
      birth_date: moment(this.birthDateValue).format('YYYY-MM-DD'),
    });
  }

  onChangeCities(cities: City[]) {
    const groupCities = this.splitCitiesById(cities);

    this.form.patchValue({
      city_ids: groupCities,
    });
  }

  submit() {
    if (this.isLoading) return;

    const agreement = this.form.get('agreement').value;
    if (!agreement) {
      this.apiErrors['agreement'] = 'Необходимо согласиться на обработку персональных данных';
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.apiErrors = {};
    this.familyErrors = {};

    const data = this.form.value;
    data['family'] = this.family;
    this.authService.updateProfile({ ...data, user_type: this.user ? this.user.user_type : null })
      .pipe(takeUntil(this.ngUnsubscribe))
      .finally(() => this.isLoading = false)
      .subscribe(user => {
        this.updatedSuccessfuly.next();
      }, error => {
        const json = error.error;
        if (error.status !== 400 || !json) {
          this.error = `Не удалось сохранить данные. Попробуйте еще раз. (${+error.status})`;
          return;
        }

        this.familyErrors = json['family'];

        this.error = head(json['non_field_errors']);
        this.apiErrors = flatten(json);
      });
  }

  private splitCitiesById(cities: City[]) {
    const sortedCitiesById = [];
    for (const city of cities) {
      sortedCitiesById.push(city.id);
    }
    return sortedCitiesById;
  }

  private joinCities(ids: number[], names: string[]): City[] {
    const joinedCities: City[] = [];
    for (let i = 0; i < ids.length; i++) {
      joinedCities.push({id: ids[i], name: names[i]});
    }
    return joinedCities;
  }

  patchPicture(picture?: Picture) {
    const data: User = { photo_id: picture ? picture.id : null };
    this.form.patchValue(data);
    data['id'] = this.user.id;
    this.isLoading = true;
    this.authService.updateProfile({ ...data, user_type: this.user.user_type, email: this.user.email })
      .pipe(takeUntil(this.ngUnsubscribe))
      .finally(() => {
        this.isLoading = false;
        this.isUploadingPhoto = false;
      })
      .subscribe(user => {
        delete this.apiErrors['photo_id'];
      }, error => {
        this.apiErrors = flatten(error.error);
      });
  }

  navigateProfile() {
    if (this.user && this.user.user_type !== 'veteran' && this.user.missing_required_fields.length) return;
    this.router.navigateByUrl(`/${this.user.user_type}/profile`);
  }

  logout() {
    this.authService.logout();
  }
}



// WEBPACK FOOTER //
// ./src/app/accounts/user-form/user-form.component.ts